import React, { useState, useMemo } from 'react';
import { Briefcase, Plus, Trash2, ChevronLeft, CheckCircle, SkipForward } from 'lucide-react';
import countryList from 'react-select-country-list';
import { FieldError, FieldWrap, cls, Spinner, getErr } from './helpers';

const EMPTY = { nom_entreprise: '', poste: '', date_debut: '', date_fin: '', description: '', pays: 'NE' };

const StepExperiences = ({ experiences, onExperiencesChange, onFinalize, onBack, loading }) => {
    const [form, setForm]     = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const countryOptions      = useMemo(() => countryList().getData(), []);

    const validate = () => {
        const e = {};
        if (!form.nom_entreprise.trim()) e.nom_entreprise = "Le nom de l'entreprise est requis";
        if (!form.poste.trim())          e.poste          = "Le poste est requis";
        if (!form.date_debut)            e.date_debut     = "La date de début est requise";
        if (!form.description.trim())    e.description    = "La description est requise";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const addExperience = () => {
        if (!validate()) return;
        onExperiencesChange([...experiences, { ...form }]);
        setForm(EMPTY);
        setErrors({});
    };

    return (
        <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-3 flex items-start gap-2">
                <SkipForward className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                    Cette étape est <strong>facultative</strong>. Vous pouvez ajouter des expériences maintenant ou plus tard depuis votre profil.
                </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1"><Plus className="h-4 w-4" /> Ajouter une expérience</h4>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Entreprise <span className="text-red-500">*</span></label>
                        <FieldWrap icon={Briefcase}>
                            <input type="text" value={form.nom_entreprise} onChange={e => setForm(p => ({...p, nom_entreprise: e.target.value}))} className={cls(errors.nom_entreprise)} placeholder="Nom de l'entreprise" />
                        </FieldWrap>
                        <FieldError msg={getErr(errors, 'nom_entreprise')} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Poste <span className="text-red-500">*</span></label>
                        <input type="text" value={form.poste} onChange={e => setForm(p => ({...p, poste: e.target.value}))} className={cls(errors.poste, false)} placeholder="Titre du poste" />
                        <FieldError msg={getErr(errors, 'poste')} />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date début <span className="text-red-500">*</span></label>
                        <input type="date" value={form.date_debut} onChange={e => setForm(p => ({...p, date_debut: e.target.value}))} className={cls(errors.date_debut, false)} />
                        <FieldError msg={getErr(errors, 'date_debut')} />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date fin <span className="text-xs text-gray-400">(si terminé)</span></label>
                        <input type="date" value={form.date_fin} onChange={e => setForm(p => ({...p, date_fin: e.target.value}))} className={cls(false, false)} />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pays</label>
                    <select value={form.pays} onChange={e => setForm(p => ({...p, pays: e.target.value}))} className={cls(false, false)}>
                        {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                    <textarea rows={2} value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className={cls(errors.description, false)} placeholder="Missions et responsabilités" />
                    <FieldError msg={getErr(errors, 'description')} />
                </div>
                <button type="button" onClick={addExperience} className="w-full py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1 transition">
                    <Plus className="h-4 w-4" /> Ajouter cette expérience
                </button>
            </div>

            {experiences.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600">Expériences ajoutées ({experiences.length})</p>
                    {experiences.map((exp, i) => (
                        <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{exp.poste}</p>
                                <p className="text-xs text-gray-500">{exp.nom_entreprise} — {exp.date_debut}</p>
                            </div>
                            <button onClick={() => onExperiencesChange(experiences.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex gap-2 pt-1">
                <button onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition">
                    <ChevronLeft className="h-4 w-4" /> Retour
                </button>
                <button onClick={() => onFinalize(false)} disabled={loading} className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition">
                    <SkipForward className="h-4 w-4" /> Passer
                </button>
                <button onClick={() => onFinalize(true)} disabled={loading} className="flex-1 flex justify-center items-center gap-2 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg text-sm font-medium transition">
                    {loading ? <Spinner /> : <CheckCircle className="h-4 w-4" />}
                    {loading ? 'Finalisation...' : "Terminer l'inscription"}
                </button>
            </div>
        </div>
    );
};

export default StepExperiences;
